import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Button } from '@abyss/web/ui/Button';
import { Divider } from '@abyss/web/ui/Divider';
import { Flex } from '@abyss/web/ui/Flex';
import { Grid } from '@abyss/web/ui/Grid';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import {
  LAWW_PORTAL_NAME,
  LAWW_USER_TIER1,
  LAWW_USER_TIER2,
} from '../../../../common/Constants';
import { CostIndicatorPopover } from '../../../../common/DataCard/CostIndicatorPopover';
import { PortalContext } from '../../../../context/PortalContext';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useCurrentMemberIsPreEffective } from '../../../../hooks/useCurrentMemberIsPreEffective';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useGeoLocationStorage } from '../../../../hooks/useGeoLocationStorage';
import { CarePathsResponse } from '../../../../models/CarePaths';
import { ProviderLocation } from '../../../../models/ProviderDetails';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import { removeLeadingZeroes } from '../../../../utils/string.utils';
import { adobeLinkTrackEvent } from '../../../AdobeTagging/adobeLinkTrackEvent';
import { ConstantsLagoon } from '../../../ConstantsLagoon';
import { phoneOnly, tabletOnly } from '../../../ConstantsStyles';
import { CostIndicator } from '../../../CostIndicator';
import {
  CarePathCost,
  CarePathCostContainer,
  CarePathCostText,
  CarePathEstimationButtonStyles,
  CarePathResultContainer,
  CarePathResultName,
  CarePathResultsList,
  CostIndicatorStyled,
  CostIndicatorText,
} from './CarePaths.styled';

type Props = {
  carePathsData: CarePathsResponse | undefined;
  selectedLocation: ProviderLocation;
  suppressCostEstimateSection?: boolean;
};

const viewCostEstimate = (
  costEstimateLink: string,
  portalName: string,
  initiateViewEstimate: boolean,
  isGuestUser: boolean,
  basePath: string,
  enableCEX: boolean,
  enableCostEstimateInCurrentTab: boolean,
  userTier?: string,
  signInUrl?: string,
  carePathStepId?: string,
  legacySSOUrl?: string
) => {
  if (isGuestUser && signInUrl) {
    const carePathIdParam = carePathStepId
      ? `&carePathId=${carePathStepId}`
      : '';
    const carePathStepIdParam = carePathStepId
      ? `&carePathStepId=${carePathStepId}_1`
      : '';
    const callBackUrl = `${window.location.pathname}${window.location.search}${carePathIdParam}${carePathStepIdParam}&initiateViewEstimate=${initiateViewEstimate}`;
    const newSignInUrl = new URL(
      signInUrl.replace('LANDING_PAGE', callBackUrl),
      window.location.origin
    ).href;

    window.open(newSignInUrl, '_self');
  } else if (
    portalName === LAWW_PORTAL_NAME &&
    isTier2User(userTier) &&
    legacySSOUrl
  ) {
    if (enableCEX) {
      const newCEXUrl = new URL(
        basePath.replace('/provider-search', costEstimateLink),
        window.location.origin
      ).href;
      window.open(newCEXUrl, '_self');
    } else {
      const SSOUrl = legacySSOUrl.replace(
        'FPC_URL',
        encodeURIComponent(costEstimateLink)
      );
      const newSSOUrl = new URL(
        basePath.replace('/provider-search', SSOUrl),
        window.location.origin
      ).href;
      window.open(newSSOUrl, '_blank');
    }
  } else {
    window.open(
      costEstimateLink,
      enableCostEstimateInCurrentTab ? '_self' : '_blank'
    );
  }
};

const isTier2User = (userTier?: string) => userTier === LAWW_USER_TIER2;

export const CarePathsResults = ({
  carePathsData,
  selectedLocation,
  suppressCostEstimateSection,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(phoneOnly);
  const istablet = useMediaQuery(tabletOnly);
  const coverageType = useCoverageType();
  const isPreEffective = useCurrentMemberIsPreEffective();
  const { longitude, latitude, zipCode } = useGeoLocationStorage();

  const locationKey = selectedLocation?.locationKey || '';
  const locationLatitude = selectedLocation?.latitude || latitude;
  const locationLongitude = selectedLocation?.longitude || longitude;
  const locationZipcode =
    selectedLocation?.address?.postalCode.split('-')[0] || zipCode;

  const { portalData } = useContext(PortalContext);
  const typeaheadState = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      initiateViewEstimate: state.typeaheadSearchStore.initiateViewEstimate,
      carePathId: state.typeaheadSearchStore.carePathId,
    }))
  );
  const carePathsButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const initiateViewEstimate = typeaheadState?.initiateViewEstimate;
  useEffect(() => {
    if (
      initiateViewEstimate &&
      isTier2User(portalData?.portalConfig?.userTier)
    ) {
      const carePathId = typeaheadState?.carePathId;
      if (carePathId !== undefined && carePathsButtonRefs.current[carePathId]) {
        carePathsButtonRefs.current[carePathId]?.click();
      }
    }
  }, [initiateViewEstimate, portalData, typeaheadState]);
  const isGuestUser = portalData.portalConfig?.isGuestUser ?? false;
  const signInUrl = portalData.portalConfig?.signInUrl;
  const userTier = portalData?.portalConfig?.userTier;

  const [
    enableCEX,
    enableCostEstimateInCurrentTab,
    enableRemoveEstimatedCostText,
    enableVisionSuffix,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CEX_GET_FULL_COST_ESTIMATE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COST_ESTIMATE_IN_CURRENT_TAB,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_REMOVE_ESTIMATED_COST_TEXT,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VISION_SUFFIX,
  ]);

  const getCostIndicatorText = (indicatorValue) => {
    if (indicatorValue === '$')
      return t('PROVIDER_DETAILS.COST_TAB.COST_AVERAGES.BELOW_AVERAGE');
    if (indicatorValue === '$$')
      return t('PROVIDER_DETAILS.COST_TAB.COST_AVERAGES.AVERAGE');
    if (indicatorValue === '$$$')
      return t('PROVIDER_DETAILS.COST_TAB.COST_AVERAGES.ABOVE_AVERAGE');

    return '';
  };

  const getCostIndicatorColor = (indicatorValue) => {
    if (indicatorValue === '$') return '$success1';
    if (indicatorValue === '$$') return '#0248AC';
    if (indicatorValue === '$$$') return '#AA2E23';

    return '';
  };

  const getCoverageType = (coverageType) => {
    if (coverageType === 'M') return 'medical';
    if (coverageType === 'V') return 'vision';
    if (coverageType === 'D') return 'dental';
    if (coverageType === 'B') return 'behavioral';

    return '';
  };

  const getProviderId = (providerId, enableVisionSuffix) => {
    if (coverageType === 'V' && enableVisionSuffix) {
      return removeLeadingZeroes(providerId);
    }
    if (coverageType === 'V' && providerId.startsWith('0')) {
      const providerIntValue = parseInt(providerId, 10);
      return (
        providerIntValue.toString().replace('P', '') ||
        providerIntValue.toString().replace('O', '')
      );
    }
    if (coverageType === 'M' && providerId.startsWith('0')) {
      const providerIntValue = parseInt(providerId, 10);
      return providerIntValue.toString();
    }

    return providerId;
  };
  const getLocationId = (locationId, enableVisionSuffix) => {
    if (coverageType === 'V' && enableVisionSuffix) {
      return removeLeadingZeroes(locationId);
    }
    if (coverageType === 'V') {
      if (locationId.charAt(locationId.length - 1) === 'P') {
        locationId = locationId.replace(
          locationId.substring(locationId.length - 1),
          ''
        );
        return locationId;
      }
    }
    return locationId;
  };

  const carePathElementContainer = (carePath) => (
    <CarePathResultContainer
      cssProps={{ enableRemoveEstimatedCostText }}
      data-testid="care-path-result-container"
      direction={enableRemoveEstimatedCostText ? 'column' : 'row'}
      justify="space-between"
    >
      <CarePathResultName
        cssProps={{ enableRemoveEstimatedCostText }}
        data-testid="care-path-result-container-heading"
      >
        {carePath.carePathName}
      </CarePathResultName>
      {!suppressCostEstimateSection && !enableRemoveEstimatedCostText && (
        <CarePathCostContainer data-testid="care-path-estimated-average-cost">
          <CarePathCostText>
            {t('PROVIDER_DETAILS.COST_TAB.CARE_PATH.ESTIMATED_AVG_COST')}
            <CarePathCost data-testid={`care-path-cost`}>
              ${carePath?.cost || ''}
            </CarePathCost>
          </CarePathCostText>
          <Flex css={CostIndicatorStyled}>
            <CostIndicator indicatorData={carePath?.costIndicator || ''} />
            <CostIndicatorText
              color={getCostIndicatorColor(carePath?.costIndicator)}
            >
              {getCostIndicatorText(carePath?.costIndicator)}
            </CostIndicatorText>
            <CostIndicatorPopover
              callFrom="carepathresults"
              costIndicator={carePath?.costIndicator || ''}
              displayCostIndicator={false}
            />
          </Flex>
        </CarePathCostContainer>
      )}
      {!isPreEffective &&
        !(
          portalData?.portalName == LAWW_PORTAL_NAME &&
          userTier === LAWW_USER_TIER1
        ) && (
          <Button
            aria-label={`get-full-cost-estimate-for ${
              carePath.carePathName
            } estimated-average-cost-is ${
              carePath?.cost + 'dollars' || 'unavailable'
            }`}
            css={CarePathEstimationButtonStyles(enableRemoveEstimatedCostText)}
            data-testid={`full-cost-estimate`}
            onClick={() => {
              adobeLinkTrackEvent({
                name: 'full cost estimate',
                location: `body:${carePath.carePathName}`,
                type: 'internal',
              });
              viewCostEstimate(
                getCostEstimateLink(carePath, enableCEX),
                portalData?.portalName,
                true,
                isGuestUser,
                portalData?.portalConfig?.basePath,
                enableCEX,
                enableCostEstimateInCurrentTab,
                userTier,
                signInUrl,
                carePath.carePathIdentifier,
                portalData?.portalConfig?.legacySSOUrl
              );
            }}
            ref={(el) =>
              (carePathsButtonRefs.current[
                carePath.carePathIdentifier as string
              ] = el)
            }
            variant="outline"
          >
            {isGuestUser && portalData?.portalName === LAWW_PORTAL_NAME
              ? t('PROVIDER_DETAILS.COST_TAB.CARE_PATH.SIGN_IN')
              : t(
                  enableRemoveEstimatedCostText
                    ? 'PROVIDER_DETAILS.COST_TAB.CARE_PATH.GET_COST_ESTIMATE'
                    : 'PROVIDER_DETAILS.COST_TAB.CARE_PATH.GET_FULL_COST_ESTIMATE'
                )}
          </Button>
        )}
    </CarePathResultContainer>
  );

  const getCostEstimateLink = (carePath: any, enableCEX: boolean) =>
    isTier2User(portalData?.portalConfig?.userTier)
      ? `${enableCEX ? '/costestimate' : ''}/carePath/${
          carePath.carePathIdentifier
        }?coverageType=${getCoverageType(
          coverageType
        )}&providerId=${getProviderId(
          locationKey.split('-')[0],
          enableVisionSuffix
        )}&locationId=${getLocationId(
          locationKey.split('-')[3],
          enableVisionSuffix
        )}&zipCode=${locationZipcode}&providerType=person&carePathStepId=${
          carePath.carePathIdentifier
        }_1&lat=${locationLatitude}&long=${locationLongitude}&source=psx`
      : `${enableCEX ? '/costestimate' : '/care'}/carePath/${
          carePath.carePathIdentifier
        }?coverageType=${getCoverageType(
          coverageType
        )}&providerId=${getProviderId(
          locationKey.split('-')[0],
          enableVisionSuffix
        )}&locationId=${getLocationId(
          locationKey.split('-')[3],
          enableVisionSuffix
        )}&zipCode=${locationZipcode}&providerType=person&carePathStepId=${
          carePath.carePathIdentifier
        }_1&lat=${locationLatitude}&long=${locationLongitude}`;

  const gridOfCarePaths = (
    <>
      <Grid>
        {carePathsData?.carePathSteps?.map((carePath, i) => (
          <>
            <Grid.Col span={{ xs: '100%', lg: '50%' }}>
              {carePathElementContainer(carePath)}
            </Grid.Col>

            {(istablet ||
              isMobile ||
              (carePathsData?.carePathSteps && i % 2 === 1)) && (
              <>
                <Divider
                  color="#E5E5E6"
                  margin="12px"
                  orientation="horizontal"
                />
              </>
            )}
          </>
        ))}
      </Grid>
    </>
  );

  const listOfCarePaths = carePathsData?.carePathSteps?.map(
    (carePath, index) => (
      <li
        data-testid={`care-path-${carePath.carePathName}`}
        key={`care-path-${carePath.carePathName}-${index}`}
      >
        {!isMobile && index === 0 && (
          <Divider color="#E5E5E6" margin="$lg" orientation="horizontal" />
        )}
        {carePathElementContainer(carePath)}
        {carePathsData?.carePathSteps &&
          index <= carePathsData?.carePathSteps.length - 1 && (
            <Divider color="#E5E5E6" margin="$lg" orientation="horizontal" />
          )}
      </li>
    )
  );

  return (
    <React.Fragment>
      <CarePathResultsList
        cssProps={{ enableRemoveEstimatedCostText }}
        data-testid="single-referral-banner"
      >
        {enableRemoveEstimatedCostText ? gridOfCarePaths : listOfCarePaths}
      </CarePathResultsList>
    </React.Fragment>
  );
};
